import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAdminAccess } from "../Analytics/shared/AdminAccess/AdminAccessContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

type AccountOption = {
  id: string;
  label: string;
};

type DriverRow = {
  id?: string;
  _id?: string;
  name?: string;
  whatsappNumber?: string;
  phoneNumber?: string;
  vehicleType?: string;
  status?: string;
  currentShiftStatus?: string;
  currentJobsCount?: number;
  maxConcurrentJobs?: number;
  serviceCategories?: string[];
  ownerUserId?: string;
  ownerId?: string;
  sellerUserId?: string;
  accountUserId?: string;
  businessUserId?: string;
  merchantUserId?: string;
  vendorUserId?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
};

type AssignmentFilter = "all" | "assigned" | "unassigned" | "unassignable";
type BulkAction = "assign" | "unassign" | "";

const clean = (value: unknown) => String(value || "").trim();

const normalizeStatus = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase();

const UNASSIGNABLE_DRIVER_STATUSES = new Set([
  "pending_dispatch",
  "notified",
  "claimed",
  "assigned",
  "arrived_at_pickup",
]);

const canUnassignJob = (row: any) =>
  Boolean(row?.assignedDriverId) &&
  UNASSIGNABLE_DRIVER_STATUSES.has(normalizeStatus(row?.status));

const statusColorScheme = (value: unknown) => {
  const status = normalizeStatus(value);

  if (["delivered", "returned_to_sender"].includes(status)) return "green";
  if (["cancelled", "expired", "failed"].includes(status)) return "red";
  if (["pending_dispatch", "notified", "claimed"].includes(status))
    return "orange";
  if (
    [
      "assigned",
      "arrived_at_pickup",
      "picked_up",
      "in_transit",
      "arrived_at_delivery",
      "proof_of_delivery_pending",
    ].includes(status)
  ) {
    return "blue";
  }

  return "gray";
};

const prettifyStatus = (value: unknown) =>
  clean(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.users)) return payload.data.users;

  return [];
};

const getAccountId = (account: any) =>
  clean(
    account?.uid ||
      account?.firebaseUid ||
      account?.userId ||
      account?._id ||
      account?.id,
  );

const getAccountLabel = (account: any) =>
  clean(
    account?.businessName ||
      account?.companyName ||
      account?.displayName ||
      account?.name ||
      account?.email ||
      getAccountId(account),
  );

const getRecordAccountIds = (row: any) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  [
    row?.ownerUserId,
    row?.ownerId,
    row?.sellerUserId,
    row?.sellerId,
    row?.accountUserId,
    row?.businessUserId,
    row?.merchantUserId,
    row?.vendorUserId,
    row?.createdBy,
    row?.createdByUserId,
    row?.senderUserId,
    row?.requestedBy,
    row?.requesterUserId,
    row?.warehouseOwnerUserId,
    row?.sourceOwnerUserId,
    row?.warehouse?.ownerUserId,
    row?.warehouse?.ownerId,
    row?.warehouse?.accountUserId,
    row?.metadata?.ownerUserId,
    row?.metadata?.ownerId,
    row?.metadata?.sellerUserId,
    row?.metadata?.sellerId,
    row?.metadata?.accountUserId,
    row?.metadata?.businessUserId,
    row?.metadata?.merchantUserId,
    row?.metadata?.vendorUserId,
    row?.metadata?.createdBy,
    row?.metadata?.createdByUserId,
    row?.metadata?.senderUserId,
    row?.metadata?.requestedBy,
    row?.metadata?.requesterUserId,
    row?.metadata?.warehouseOwnerUserId,
    row?.metadata?.sourceOwnerUserId,
  ].forEach(add);

  if (ids.size === 0) {
    add(row?.userId);
    add(row?.metadata?.userId);
  }

  return ids;
};

const belongsToAccount = (row: any, accountId: string) => {
  const id = clean(accountId);
  if (!id) return true;
  return getRecordAccountIds(row).has(id);
};

const runInBatches = async <T,>(
  items: T[],
  action: (item: T) => Promise<unknown>,
  batchSize = 5,
) => {
  let succeeded = 0;
  let failed = 0;

  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    const results = await Promise.allSettled(batch.map(action));

    results.forEach((result) => {
      if (result.status === "fulfilled") succeeded += 1;
      else failed += 1;
    });
  }

  return { succeeded, failed };
};

const DispatchQueuePage: React.FC = () => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const { userId, isSuperAdmin, loading: accessLoading } = useAdminAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  const [jobSearch, setJobSearch] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] =
    useState<AssignmentFilter>("all");

  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [bulkDriverSearch, setBulkDriverSearch] = useState("");
  const [bulkDriverId, setBulkDriverId] = useState("");
  const [bulkAction, setBulkAction] = useState<BulkAction>("");

  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [activeJobActionId, setActiveJobActionId] = useState("");

  const getDriverId = (driver: DriverRow) => clean(driver?.id || driver?._id);

  const loadAccounts = async (jobs: any[]) => {
    if (!isSuperAdmin) {
      const ownId = clean(userId);
      setAccounts(ownId ? [{ id: ownId, label: ownId }] : []);
      setSelectedAccountId(ownId);
      return;
    }

    const accountMap = new Map<string, string>();

    try {
      const token = await getAuth().currentUser?.getIdToken();

      if (!token) {
        throw new Error("A valid Firebase authentication token is required.");
      }

      const response = await axios.get(
        `${API_BASE}/api/v1/admin/users?page=1&limit=500`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toArray(response.data).forEach((account) => {
        const id = getAccountId(account);
        if (!id) return;
        accountMap.set(id, getAccountLabel(account) || id);
      });
    } catch (error) {
      console.warn(
        "Could not load account names for dispatch filtering:",
        error,
      );
    }

    jobs.forEach((job) => {
      getRecordAccountIds(job).forEach((id) => {
        if (!accountMap.has(id)) accountMap.set(id, id);
      });
    });

    setAccounts(
      Array.from(accountMap.entries())
        .map(([id, label]) => ({ id, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    );
  };

  const load = async () => {
    if (accessLoading) return;

    if (!userId) {
      setRows([]);
      setDrivers([]);
      setAccounts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const params: Record<string, string> = {};

      if (!isSuperAdmin) {
        params.ownerUserId = userId;
        params.accountUserId = userId;
      } else if (selectedAccountId) {
        params.ownerUserId = selectedAccountId;
        params.accountUserId = selectedAccountId;
      }

      const [jobsResponse, driversResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/v1/dispatch/jobs`, { params }),
        axios.get(`${API_BASE}/api/v1/drivers`),
      ]);

      const jobs = toArray(jobsResponse.data);
      const driverRows = toArray(driversResponse.data) as DriverRow[];

      setRows(jobs);
      setDrivers(driverRows);
      await loadAccounts(jobs);
    } catch (error: any) {
      console.error("Failed to load dispatch jobs:", error);
      setRows([]);

      toast({
        title: "Failed to load dispatch jobs",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessLoading) return;

    if (!isSuperAdmin && userId) {
      setSelectedAccountId(userId);
    }

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, isSuperAdmin, userId]);

  const driverById = useMemo(() => {
    const map = new Map<string, DriverRow>();

    drivers.forEach((driver) => {
      const id = getDriverId(driver);
      if (id) map.set(id, driver);
    });

    return map;
  }, [drivers]);

  const driverNameById = useMemo(() => {
    const map = new Map<string, string>();

    drivers.forEach((driver) => {
      const id = getDriverId(driver);
      if (!id) return;

      map.set(
        id,
        clean(driver.name) ||
          clean(driver.whatsappNumber) ||
          clean(driver.phoneNumber) ||
          id,
      );
    });

    return map;
  }, [drivers]);

  const visibleRows = useMemo(() => {
    if (!userId) return [];

    if (isSuperAdmin) {
      if (!selectedAccountId) return rows;
      return rows.filter((row) => belongsToAccount(row, selectedAccountId));
    }

    return rows.filter((row) => belongsToAccount(row, userId));
  }, [isSuperAdmin, rows, selectedAccountId, userId]);

  const canManageJob = (row: any) => {
    if (!userId) return false;

    if (isSuperAdmin) {
      if (!selectedAccountId) return true;
      return belongsToAccount(row, selectedAccountId);
    }

    return belongsToAccount(row, userId);
  };

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          visibleRows
            .map((row) => normalizeStatus(row?.status))
            .filter(Boolean),
        ),
      ).sort(),
    [visibleRows],
  );

  const filteredRows = useMemo(() => {
    const jobQuery = jobSearch.trim().toLowerCase();
    const driverQuery = driverFilter.trim().toLowerCase();

    return visibleRows.filter((row) => {
      if (statusFilter && normalizeStatus(row?.status) !== statusFilter) {
        return false;
      }

      if (assignmentFilter === "assigned" && !row?.assignedDriverId)
        return false;
      if (assignmentFilter === "unassigned" && row?.assignedDriverId)
        return false;
      if (assignmentFilter === "unassignable" && !canUnassignJob(row))
        return false;

      if (driverQuery) {
        if (!row?.assignedDriverId) return false;

        const id = clean(row.assignedDriverId);
        const driver = driverById.get(id);
        const driverHaystack = [
          id,
          driverNameById.get(id),
          row?.assignedDriverName,
          driver?.name,
          driver?.whatsappNumber,
          driver?.phoneNumber,
          driver?.vehicleType,
        ]
          .map((value) => clean(value).toLowerCase())
          .join(" ");

        if (!driverHaystack.includes(driverQuery)) return false;
      }

      if (jobQuery) {
        const jobHaystack = [
          row?._id,
          row?.sourceType,
          row?.sourceId,
          row?.customerName,
          row?.customerPhone,
          row?.pickupAddress,
          row?.dropoffAddress,
          row?.warehouseName,
          row?.warehouseId,
          row?.status,
          row?.assignedDriverId,
          row?.assignedDriverName,
          row?.assignedDriverId
            ? driverNameById.get(clean(row.assignedDriverId))
            : "",
        ]
          .map((value) => clean(value).toLowerCase())
          .join(" ");

        if (!jobHaystack.includes(jobQuery)) return false;
      }

      return true;
    });
  }, [
    assignmentFilter,
    driverById,
    driverFilter,
    driverNameById,
    jobSearch,
    statusFilter,
    visibleRows,
  ]);

  const scopedDrivers = useMemo(() => {
    const accountId = isSuperAdmin ? selectedAccountId : userId;

    return drivers
      .filter((driver) => normalizeStatus(driver.status) !== "suspended")
      .filter((driver) => !accountId || belongsToAccount(driver, accountId));
  }, [drivers, isSuperAdmin, selectedAccountId, userId]);

  const searchDriverRows = (queryValue: string) => {
    const query = queryValue.trim().toLowerCase();

    return scopedDrivers
      .filter((driver) => {
        if (!query) return true;

        return [
          driver.name,
          driver.whatsappNumber,
          driver.phoneNumber,
          driver.vehicleType,
          driver.status,
          driver.currentShiftStatus,
          ...(Array.isArray(driver.serviceCategories)
            ? driver.serviceCategories
            : []),
        ]
          .map((value) => clean(value).toLowerCase())
          .join(" ")
          .includes(query);
      })
      .sort((a, b) => clean(a.name).localeCompare(clean(b.name)));
  };

  const searchedDrivers = useMemo(
    () => searchDriverRows(driverSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [driverSearch, scopedDrivers],
  );

  const bulkDrivers = useMemo(
    () => searchDriverRows(bulkDriverSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bulkDriverSearch, scopedDrivers],
  );

  const selectableFilteredRows = useMemo(
    () => filteredRows.filter((row) => canManageJob(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRows, isSuperAdmin, selectedAccountId, userId],
  );

  const selectedRows = useMemo(() => {
    const selected = new Set(selectedJobIds);
    return filteredRows.filter((row) => selected.has(clean(row?._id)));
  }, [filteredRows, selectedJobIds]);

  const bulkAssignableRows = useMemo(
    () =>
      selectedRows.filter((row) => !row?.assignedDriverId && canManageJob(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows, isSuperAdmin, selectedAccountId, userId],
  );

  const bulkUnassignableRows = useMemo(
    () =>
      selectedRows.filter((row) => canManageJob(row) && canUnassignJob(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows, isSuperAdmin, selectedAccountId, userId],
  );

  const assignedCount = useMemo(
    () => visibleRows.filter((row) => Boolean(row?.assignedDriverId)).length,
    [visibleRows],
  );

  const unassignedCount = visibleRows.length - assignedCount;
  const allFilteredSelected =
    selectableFilteredRows.length > 0 &&
    selectableFilteredRows.every((row) =>
      selectedJobIds.includes(clean(row?._id)),
    );

  const clearSelection = () => setSelectedJobIds([]);

  const resetFilters = () => {
    setJobSearch("");
    setDriverFilter("");
    setStatusFilter("");
    setAssignmentFilter("all");
    clearSelection();
  };

  const toggleRowSelection = (row: any) => {
    if (!canManageJob(row)) return;

    const id = clean(row?._id);
    if (!id) return;

    setSelectedJobIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  };

  const toggleSelectAllFiltered = () => {
    const ids = selectableFilteredRows
      .map((row) => clean(row?._id))
      .filter(Boolean);

    if (allFilteredSelected) {
      setSelectedJobIds((current) => current.filter((id) => !ids.includes(id)));
      return;
    }

    setSelectedJobIds((current) => Array.from(new Set([...current, ...ids])));
  };

  const getAuthConfig = async () => {
    const token = await getAuth().currentUser?.getIdToken();
    return token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : undefined;
  };

  const openDriverModal = (row: any) => {
    if (!row?._id || !canManageJob(row)) {
      toast({
        title: "Access denied",
        description:
          "You can manage drivers only for jobs in the selected account.",
        status: "error",
      });
      return;
    }

    setSelectedJob(row);
    setSelectedDriverId(clean(row.assignedDriverId));
    setDriverSearch("");
    onOpen();
  };

  const closeDriverModal = () => {
    if (activeJobActionId) return;
    setSelectedJob(null);
    setSelectedDriverId("");
    setDriverSearch("");
    onClose();
  };

  const assignDriver = async () => {
    if (!selectedJob?._id || !selectedDriverId) {
      toast({ title: "Select a driver first", status: "warning" });
      return;
    }

    try {
      setActiveJobActionId(clean(selectedJob._id));
      const config = await getAuthConfig();

      await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${selectedJob._id}/claim`,
        {
          driverId: selectedDriverId,
          assignmentSource: "admin_assignment",
          allowNonCandidate: true,
          overrideEligibility: true,
        },
        config,
      );

      toast({ title: "Driver assigned", status: "success" });
      setSelectedJob(null);
      setSelectedDriverId("");
      setDriverSearch("");
      onClose();
      await load();
    } catch (error: any) {
      toast({
        title: "Failed to assign driver",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
      });
    } finally {
      setActiveJobActionId("");
    }
  };

  const unassignDriver = async (row = selectedJob) => {
    if (!row?._id || !row?.assignedDriverId || !canManageJob(row)) return;

    if (!canUnassignJob(row)) {
      toast({
        title: "Driver cannot be unassigned",
        description:
          "Pickup or delivery movement has already progressed. Use the normal cancel, return, or status workflow instead.",
        status: "warning",
        duration: 4500,
        isClosable: true,
      });
      return;
    }

    const driverLabel =
      driverNameById.get(clean(row.assignedDriverId)) ||
      row.assignedDriverName ||
      row.assignedDriverId;

    const confirmed = window.confirm(
      `Unassign ${driverLabel} from this job?\n\nThe job will return to Pending Dispatch. The order is not deleted or cancelled.`,
    );

    if (!confirmed) return;

    try {
      setActiveJobActionId(clean(row._id));
      const config = await getAuthConfig();

      await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${row._id}/unassign`,
        {
          reason: "dispatch_queue_admin_unassign",
          refreshCandidates: true,
          notifyDrivers: false,
        },
        config,
      );

      toast({ title: "Driver unassigned", status: "success" });

      if (selectedJob?._id === row._id) {
        setSelectedJob(null);
        setSelectedDriverId("");
        setDriverSearch("");
        onClose();
      }

      setSelectedJobIds((current) =>
        current.filter((id) => id !== clean(row._id)),
      );
      await load();
    } catch (error: any) {
      toast({
        title: "Failed to unassign driver",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
      });
    } finally {
      setActiveJobActionId("");
    }
  };

  const bulkAssign = async () => {
    if (!bulkDriverId) {
      toast({
        title: "Choose a driver for bulk assignment",
        status: "warning",
      });
      return;
    }

    if (!bulkAssignableRows.length) {
      toast({
        title: "No unassigned jobs selected",
        description: "Select one or more unassigned jobs first.",
        status: "info",
      });
      return;
    }

    const driverLabel = driverNameById.get(bulkDriverId) || bulkDriverId;
    const skipped = selectedRows.length - bulkAssignableRows.length;
    const confirmed = window.confirm(
      `Assign ${bulkAssignableRows.length} selected job${bulkAssignableRows.length === 1 ? "" : "s"} to ${driverLabel}?${
        skipped > 0
          ? `\n\n${skipped} selected job${skipped === 1 ? " is" : "s are"} already assigned and will be skipped.`
          : ""
      }`,
    );

    if (!confirmed) return;

    try {
      setBulkAction("assign");
      const config = await getAuthConfig();

      const result = await runInBatches(bulkAssignableRows, (row) =>
        axios.post(
          `${API_BASE}/api/v1/dispatch/jobs/${row._id}/claim`,
          {
            driverId: bulkDriverId,
            assignmentSource: "admin_bulk_assignment",
            allowNonCandidate: true,
            overrideEligibility: true,
          },
          config,
        ),
      );

      toast({
        title:
          result.failed === 0
            ? `${result.succeeded} job${result.succeeded === 1 ? "" : "s"} assigned`
            : "Bulk assignment completed with some failures",
        description:
          result.failed > 0
            ? `${result.succeeded} assigned, ${result.failed} failed.${
                skipped > 0 ? ` ${skipped} already-assigned jobs skipped.` : ""
              }`
            : skipped > 0
              ? `${skipped} already-assigned job${skipped === 1 ? " was" : "s were"} skipped.`
              : undefined,
        status: result.failed === 0 ? "success" : "warning",
        duration: 5000,
        isClosable: true,
      });

      clearSelection();
      setBulkDriverId("");
      setBulkDriverSearch("");
      await load();
    } catch (error: any) {
      toast({
        title: "Bulk assignment failed",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
      });
    } finally {
      setBulkAction("");
    }
  };

  const bulkUnassign = async () => {
    if (!bulkUnassignableRows.length) {
      toast({
        title: "No selected jobs can be unassigned",
        description:
          "Only assigned jobs that have not progressed beyond pickup can be unassigned.",
        status: "info",
      });
      return;
    }

    const skipped = selectedRows.length - bulkUnassignableRows.length;
    const confirmed = window.confirm(
      `Unassign drivers from ${bulkUnassignableRows.length} selected job${bulkUnassignableRows.length === 1 ? "" : "s"}?\n\nThese jobs will return to Pending Dispatch.${
        skipped > 0
          ? `\n${skipped} selected job${skipped === 1 ? " is" : "s are"} not eligible and will be skipped.`
          : ""
      }`,
    );

    if (!confirmed) return;

    try {
      setBulkAction("unassign");
      const config = await getAuthConfig();

      const result = await runInBatches(bulkUnassignableRows, (row) =>
        axios.post(
          `${API_BASE}/api/v1/dispatch/jobs/${row._id}/unassign`,
          {
            reason: "dispatch_queue_admin_bulk_unassign",
            refreshCandidates: true,
            notifyDrivers: false,
          },
          config,
        ),
      );

      toast({
        title:
          result.failed === 0
            ? `${result.succeeded} job${result.succeeded === 1 ? "" : "s"} unassigned`
            : "Bulk unassignment completed with some failures",
        description:
          result.failed > 0
            ? `${result.succeeded} unassigned, ${result.failed} failed.${
                skipped > 0 ? ` ${skipped} ineligible jobs skipped.` : ""
              }`
            : skipped > 0
              ? `${skipped} ineligible job${skipped === 1 ? " was" : "s were"} skipped.`
              : undefined,
        status: result.failed === 0 ? "success" : "warning",
        duration: 5000,
        isClosable: true,
      });

      clearSelection();
      await load();
    } catch (error: any) {
      toast({
        title: "Bulk unassignment failed",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
      });
    } finally {
      setBulkAction("");
    }
  };

  if (accessLoading) {
    return (
      <Box p={{ base: 0, md: 2 }} bg={theme.background} minH="100%">
        <Flex minH="180px" align="center" justify="center">
          <Spinner />
        </Flex>
      </Box>
    );
  }

  if (!userId) {
    return (
      <Box p={{ base: 0, md: 2 }} bg={theme.background} minH="100%">
        <Text color={theme.textMuted}>
          Sign in before opening the dispatch queue.
        </Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 0, md: 2 }} bg={theme.background} minH="100%" minW={0}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        align={{ base: "stretch", lg: "center" }}
        justify="space-between"
        gap={4}
        mb={5}
      >
        <Box>
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="800"
            color={theme.text}
          >
            Driver Dispatch
          </Text>
          <Text color={theme.textMuted} fontSize="sm" mt={1}>
            Find jobs quickly, assign drivers, and manage safe unassignment in
            bulk.
          </Text>
        </Box>

        <Button
          variant="outline"
          onClick={() => void load()}
          isLoading={loading}
        >
          Refresh
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3} mb={4}>
        {[
          { label: "Jobs", value: visibleRows.length, scheme: "blue" },
          { label: "Assigned", value: assignedCount, scheme: "green" },
          { label: "Unassigned", value: unassignedCount, scheme: "orange" },
          { label: "Selected", value: selectedRows.length, scheme: "purple" },
        ].map((item) => (
          <Box
            key={item.label}
            p={{ base: 3, md: 4 }}
            borderRadius="14px"
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
          >
            <Text fontSize="xs" color={theme.textMuted} fontWeight="700">
              {item.label}
            </Text>
            <HStack mt={1} justify="space-between">
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="800"
                color={theme.text}
              >
                {item.value}
              </Text>
              <Badge colorScheme={item.scheme} borderRadius="full" px={2}>
                {item.label}
              </Badge>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      <Box
        p={{ base: 3, md: 4 }}
        mb={4}
        borderRadius="16px"
        bg={theme.surface}
        border={`1px solid ${theme.border}`}
        boxShadow={theme.shadowSm}
      >
        <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={3}>
          {isSuperAdmin ? (
            <Box>
              <Text
                mb={1.5}
                fontSize="xs"
                fontWeight="700"
                color={theme.textMuted}
              >
                Account
              </Text>
              <Select
                value={selectedAccountId}
                onChange={(event) => {
                  setSelectedAccountId(event.target.value);
                  clearSelection();
                }}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
              >
                <option value="">All accounts</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.label}
                  </option>
                ))}
              </Select>
            </Box>
          ) : null}

          <Box>
            <Text
              mb={1.5}
              fontSize="xs"
              fontWeight="700"
              color={theme.textMuted}
            >
              Search jobs
            </Text>
            <Input
              placeholder="Customer, route, job ID..."
              value={jobSearch}
              onChange={(event) => {
                setJobSearch(event.target.value);
                clearSelection();
              }}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            />
          </Box>

          <Box>
            <Text
              mb={1.5}
              fontSize="xs"
              fontWeight="700"
              color={theme.textMuted}
            >
              Driver name
            </Text>
            <Input
              placeholder="Filter by driver name or phone"
              value={driverFilter}
              onChange={(event) => {
                setDriverFilter(event.target.value);
                clearSelection();
              }}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            />
          </Box>

          <Box>
            <Text
              mb={1.5}
              fontSize="xs"
              fontWeight="700"
              color={theme.textMuted}
            >
              Status
            </Text>
            <Select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                clearSelection();
              }}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            >
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {prettifyStatus(status)}
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text
              mb={1.5}
              fontSize="xs"
              fontWeight="700"
              color={theme.textMuted}
            >
              Assignment
            </Text>
            <HStack>
              <Select
                value={assignmentFilter}
                onChange={(event) => {
                  setAssignmentFilter(event.target.value as AssignmentFilter);
                  clearSelection();
                }}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
              >
                <option value="all">All jobs</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
                <option value="unassignable">Can unassign</option>
              </Select>
              <Button variant="ghost" onClick={resetFilters} flexShrink={0}>
                Clear
              </Button>
            </HStack>
          </Box>
        </SimpleGrid>

        <Flex mt={3} justify="space-between" align="center" gap={3} wrap="wrap">
          <Text fontSize="sm" color={theme.textMuted}>
            Showing{" "}
            <Text as="span" fontWeight="800" color={theme.text}>
              {filteredRows.length}
            </Text>{" "}
            of {visibleRows.length} jobs
          </Text>
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleSelectAllFiltered}
            isDisabled={!selectableFilteredRows.length}
          >
            {allFilteredSelected
              ? "Clear visible selection"
              : "Select all visible"}
          </Button>
        </Flex>
      </Box>

      {selectedRows.length > 0 ? (
        <Box
          p={{ base: 3, md: 4 }}
          mb={4}
          borderRadius="16px"
          bg={theme.surfaceAlt}
          border={`1px solid ${theme.border}`}
        >
          <Flex
            direction={{ base: "column", xl: "row" }}
            align={{ base: "stretch", xl: "flex-end" }}
            gap={3}
          >
            <Box minW={{ xl: "180px" }}>
              <Text
                fontSize="xs"
                fontWeight="700"
                color={theme.textMuted}
                mb={1}
              >
                Bulk actions
              </Text>
              <Text fontWeight="800" color={theme.text}>
                {selectedRows.length} selected
              </Text>
              <Text fontSize="xs" color={theme.textMuted}>
                {bulkAssignableRows.length} assignable ·{" "}
                {bulkUnassignableRows.length} can unassign
              </Text>
            </Box>

            <Box flex="1" minW={{ xl: "220px" }}>
              <Text
                fontSize="xs"
                fontWeight="700"
                color={theme.textMuted}
                mb={1}
              >
                Find driver
              </Text>
              <Input
                placeholder="Search driver name or phone"
                value={bulkDriverSearch}
                onChange={(event) => {
                  setBulkDriverSearch(event.target.value);
                  setBulkDriverId("");
                }}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
              />
            </Box>

            <Box flex="1" minW={{ xl: "260px" }}>
              <Text
                fontSize="xs"
                fontWeight="700"
                color={theme.textMuted}
                mb={1}
              >
                Assign selected to
              </Text>
              <Select
                placeholder={
                  bulkDrivers.length ? "Select driver" : "No drivers found"
                }
                value={bulkDriverId}
                onChange={(event) => setBulkDriverId(event.target.value)}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
              >
                {bulkDrivers.map((driver) => {
                  const id = getDriverId(driver);
                  if (!id) return null;

                  return (
                    <option key={id} value={id}>
                      {driver.name || id} ·{" "}
                      {driver.currentShiftStatus || "off_shift"} ·{" "}
                      {Number(driver.currentJobsCount || 0)}/
                      {Number(driver.maxConcurrentJobs || 2)} jobs
                    </option>
                  );
                })}
              </Select>
            </Box>

            <HStack align="stretch" flexWrap="wrap">
              <Button
                onClick={() => void bulkAssign()}
                isLoading={bulkAction === "assign"}
                isDisabled={
                  !bulkDriverId ||
                  !bulkAssignableRows.length ||
                  Boolean(bulkAction)
                }
              >
                Assign {bulkAssignableRows.length || "selected"}
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={() => void bulkUnassign()}
                isLoading={bulkAction === "unassign"}
                isDisabled={!bulkUnassignableRows.length || Boolean(bulkAction)}
              >
                Unassign {bulkUnassignableRows.length || "selected"}
              </Button>
              <Button
                variant="ghost"
                onClick={clearSelection}
                isDisabled={Boolean(bulkAction)}
              >
                Clear selection
              </Button>
            </HStack>
          </Flex>
        </Box>
      ) : null}

      <Box
        borderRadius="16px"
        bg={theme.surface}
        border={`1px solid ${theme.border}`}
        boxShadow={theme.shadowSm}
        overflow="hidden"
      >
        {loading ? (
          <Flex
            minH="220px"
            align="center"
            justify="center"
            direction="column"
            gap={3}
          >
            <Spinner />
            <Text color={theme.textMuted} fontSize="sm">
              Loading dispatch jobs...
            </Text>
          </Flex>
        ) : filteredRows.length === 0 ? (
          <Box py={12} px={4} textAlign="center">
            <Text fontWeight="800" color={theme.text}>
              No matching jobs
            </Text>
            <Text mt={1} color={theme.textMuted} fontSize="sm">
              Try clearing a filter or searching another driver.
            </Text>
            <Button mt={4} size="sm" variant="outline" onClick={resetFilters}>
              Clear filters
            </Button>
          </Box>
        ) : (
          <>
            <Stack display={{ base: "flex", md: "none" }} spacing={0}>
              {filteredRows.map((row) => {
                const id = clean(row?._id);
                const assignedDriverId = clean(row?.assignedDriverId);
                const assignedDriverName = assignedDriverId
                  ? driverNameById.get(assignedDriverId) ||
                    row?.assignedDriverName ||
                    assignedDriverId
                  : "Unassigned";
                const checked = selectedJobIds.includes(id);

                return (
                  <Box
                    key={id}
                    p={4}
                    borderBottom="1px solid"
                    borderColor={theme.border}
                    bg={checked ? theme.surfaceAlt : theme.surface}
                  >
                    <Flex justify="space-between" align="start" gap={3}>
                      <HStack align="start" spacing={3} minW={0}>
                        <Checkbox
                          mt={1}
                          isChecked={checked}
                          onChange={() => toggleRowSelection(row)}
                          isDisabled={!canManageJob(row)}
                        />
                        <Box minW={0}>
                          <Text
                            fontWeight="800"
                            color={theme.text}
                            noOfLines={1}
                          >
                            {row?.customerName ||
                              row?.sourceType ||
                              "Dispatch job"}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            noOfLines={1}
                          >
                            {row?.customerPhone || row?.sourceId || id}
                          </Text>
                        </Box>
                      </HStack>
                      <Badge
                        colorScheme={statusColorScheme(row?.status)}
                        borderRadius="full"
                      >
                        {prettifyStatus(row?.status)}
                      </Badge>
                    </Flex>

                    <SimpleGrid columns={1} spacing={2} mt={3}>
                      <Box>
                        <Text fontSize="xs" color={theme.textMuted}>
                          Driver
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={theme.text}>
                          {assignedDriverName}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={theme.textMuted}>
                          Route
                        </Text>
                        <Text fontSize="sm" color={theme.text} noOfLines={2}>
                          {row?.pickupAddress || "Pickup not set"} →{" "}
                          {row?.dropoffAddress || "Dropoff not set"}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <HStack mt={4} spacing={2}>
                      <Button
                        size="sm"
                        flex="1"
                        variant="outline"
                        onClick={() => openDriverModal(row)}
                        isDisabled={!canManageJob(row)}
                      >
                        {assignedDriverId ? "Manage driver" : "Assign driver"}
                      </Button>
                      {assignedDriverId && canUnassignJob(row) ? (
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => void unassignDriver(row)}
                          isLoading={activeJobActionId === id}
                        >
                          Unassign
                        </Button>
                      ) : null}
                    </HStack>
                  </Box>
                );
              })}
            </Stack>

            <Box display={{ base: "none", md: "block" }} overflowX="auto">
              <Table size="sm" minW="900px">
                <Thead bg={theme.surfaceAlt}>
                  <Tr>
                    <Th w="46px">
                      <Checkbox
                        isChecked={allFilteredSelected}
                        isIndeterminate={
                          selectedRows.length > 0 && !allFilteredSelected
                        }
                        onChange={toggleSelectAllFiltered}
                        isDisabled={!selectableFilteredRows.length}
                      />
                    </Th>
                    <Th>Job / Customer</Th>
                    <Th>Status</Th>
                    <Th>Driver</Th>
                    <Th>Route</Th>
                    <Th textAlign="right">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredRows.map((row) => {
                    const id = clean(row?._id);
                    const assignedDriverId = clean(row?.assignedDriverId);
                    const assignedDriverName = assignedDriverId
                      ? driverNameById.get(assignedDriverId) ||
                        row?.assignedDriverName ||
                        assignedDriverId
                      : "Unassigned";
                    const checked = selectedJobIds.includes(id);

                    return (
                      <Tr
                        key={id}
                        bg={checked ? theme.surfaceAlt : undefined}
                        _hover={{ bg: theme.surfaceAlt }}
                      >
                        <Td>
                          <Checkbox
                            isChecked={checked}
                            onChange={() => toggleRowSelection(row)}
                            isDisabled={!canManageJob(row)}
                          />
                        </Td>
                        <Td maxW="240px">
                          <Text
                            fontWeight="800"
                            color={theme.text}
                            noOfLines={1}
                          >
                            {row?.customerName ||
                              row?.sourceType ||
                              "Dispatch job"}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            noOfLines={1}
                          >
                            {row?.customerPhone || row?.sourceId || id}
                          </Text>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={statusColorScheme(row?.status)}
                            borderRadius="full"
                          >
                            {prettifyStatus(row?.status)}
                          </Badge>
                        </Td>
                        <Td maxW="220px">
                          <Text
                            fontWeight={assignedDriverId ? "700" : "500"}
                            color={
                              assignedDriverId ? theme.text : theme.textMuted
                            }
                            noOfLines={1}
                          >
                            {assignedDriverName}
                          </Text>
                        </Td>
                        <Td maxW="360px">
                          <Text fontSize="sm" color={theme.text} noOfLines={1}>
                            {row?.pickupAddress || "Pickup not set"}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            noOfLines={1}
                          >
                            → {row?.dropoffAddress || "Dropoff not set"}
                          </Text>
                        </Td>
                        <Td>
                          <HStack justify="flex-end" spacing={1}>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => openDriverModal(row)}
                              isDisabled={!canManageJob(row)}
                            >
                              {assignedDriverId ? "Manage" : "Assign"}
                            </Button>
                            {assignedDriverId && canUnassignJob(row) ? (
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => void unassignDriver(row)}
                                isLoading={activeJobActionId === id}
                              >
                                Unassign
                              </Button>
                            ) : null}
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={closeDriverModal} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>
            {selectedJob?.assignedDriverId ? "Manage driver" : "Assign driver"}
          </ModalHeader>
          <ModalCloseButton isDisabled={Boolean(activeJobActionId)} />

          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box
                p={3}
                borderRadius="12px"
                bg={theme.surfaceAlt}
                border={`1px solid ${theme.border}`}
              >
                <HStack justify="space-between" align="start">
                  <Box minW={0}>
                    <Text fontSize="xs" color={theme.textMuted}>
                      Job
                    </Text>
                    <Text fontWeight="800" color={theme.text} noOfLines={1}>
                      {selectedJob?.customerName ||
                        selectedJob?.sourceType ||
                        "Dispatch job"}
                    </Text>
                    <Text fontSize="xs" color={theme.textMuted} noOfLines={1}>
                      {selectedJob?.customerPhone ||
                        selectedJob?.sourceId ||
                        selectedJob?._id}
                    </Text>
                  </Box>
                  <Badge
                    colorScheme={statusColorScheme(selectedJob?.status)}
                    borderRadius="full"
                  >
                    {prettifyStatus(selectedJob?.status)}
                  </Badge>
                </HStack>
              </Box>

              {selectedJob?.assignedDriverId ? (
                <Box>
                  <Text fontSize="xs" color={theme.textMuted}>
                    Current driver
                  </Text>
                  <Text
                    mt={1}
                    fontSize="lg"
                    fontWeight="800"
                    color={theme.text}
                  >
                    {driverNameById.get(clean(selectedJob.assignedDriverId)) ||
                      selectedJob.assignedDriverName ||
                      selectedJob.assignedDriverId}
                  </Text>

                  {canUnassignJob(selectedJob) ? (
                    <Text mt={1} fontSize="sm" color={theme.textMuted}>
                      You can safely return this job to Pending Dispatch and
                      assign another driver.
                    </Text>
                  ) : (
                    <Text mt={1} fontSize="sm" color={theme.textMuted}>
                      Unassignment is locked because pickup or delivery has
                      already progressed.
                    </Text>
                  )}
                </Box>
              ) : (
                <>
                  <Box>
                    <Text
                      mb={1.5}
                      fontSize="xs"
                      fontWeight="700"
                      color={theme.textMuted}
                    >
                      Find driver
                    </Text>
                    <Input
                      placeholder="Search driver name, phone or vehicle"
                      value={driverSearch}
                      onChange={(event) => {
                        setDriverSearch(event.target.value);
                        setSelectedDriverId("");
                      }}
                      bg={theme.inputBg}
                      borderColor={theme.inputBorder}
                    />
                  </Box>

                  <Box>
                    <Text
                      mb={1.5}
                      fontSize="xs"
                      fontWeight="700"
                      color={theme.textMuted}
                    >
                      Driver
                    </Text>
                    <Select
                      placeholder={
                        searchedDrivers.length
                          ? "Select driver"
                          : "No drivers found"
                      }
                      value={selectedDriverId}
                      onChange={(event) =>
                        setSelectedDriverId(event.target.value)
                      }
                      bg={theme.inputBg}
                      borderColor={theme.inputBorder}
                    >
                      {searchedDrivers.map((driver) => {
                        const id = getDriverId(driver);
                        if (!id) return null;

                        return (
                          <option key={id} value={id}>
                            {driver.name || id} ·{" "}
                            {driver.currentShiftStatus || "off_shift"} ·{" "}
                            {Number(driver.currentJobsCount || 0)}/
                            {Number(driver.maxConcurrentJobs || 2)} jobs
                          </option>
                        );
                      })}
                    </Select>
                  </Box>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack w="100%" justify="flex-end">
              <Button
                variant="ghost"
                onClick={closeDriverModal}
                isDisabled={Boolean(activeJobActionId)}
              >
                Close
              </Button>

              {selectedJob?.assignedDriverId ? (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => void unassignDriver(selectedJob)}
                  isLoading={activeJobActionId === clean(selectedJob?._id)}
                  isDisabled={!canUnassignJob(selectedJob)}
                >
                  Unassign driver
                </Button>
              ) : (
                <Button
                  onClick={() => void assignDriver()}
                  isLoading={activeJobActionId === clean(selectedJob?._id)}
                  isDisabled={!selectedDriverId}
                >
                  Assign driver
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DispatchQueuePage;
